import React, { useState, useEffect } from "react";
import { Box, Button, useTheme, CircularProgress } from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useResultService from "../../api/services/ResultsService";
import { toast } from "react-hot-toast";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Header from "../../components/Header";

const Subject_Results = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { teacherId, subjectId, clazzId, examinationTypeId } = useParams();
  const [searchParams] = useSearchParams();

  // ✅ Lock query params
  const [queryValues] = useState(() => {
    const examMarksParam = searchParams.get("examMarks") || "";
    const statusParam = searchParams.get("status") || "";
    return {
      examMarks: examMarksParam ? Number(examMarksParam) : null,
      status: statusParam,
    };
  });

  const { examMarks, status } = queryValues;
  const isEditingDisabled = status === "PUBLISHED";

  const {
    subject_results,
    fetch_subject_results,
    updateSubjectResultsDetails,
    loadResultsDetails,
  } = useResultService();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editedRows, setEditedRows] = useState({});
  const [rows, setRows] = useState([]);

  // ✅ Load subject results
  useEffect(() => {
    const loadData = async () => {
      if (teacherId && subjectId && clazzId && examinationTypeId) {
        setIsLoading(true);
        await fetch_subject_results({
          teacherId,
          subjectId,
          clazzId,
          examinationTypeId,
        });
        setIsLoading(false);
      }
    };
    loadData();
  }, [teacherId, subjectId, clazzId, examinationTypeId]);

  // ✅ Update local rows when subject_results changes
  useEffect(() => {
    if (subject_results) {
      const cleaned = subject_results
        .map((r) => ({
          ...r,
          combination: r.combination?.trim().toUpperCase(),
          studentName: r.studentName?.trim().toUpperCase(),
        }))
        .sort((a, b) => {
          const combA = a.combination || "";
          const combB = b.combination || "";
          if (combA < combB) return -1;
          if (combA > combB) return 1;
          return a.studentName.localeCompare(b.studentName);
        });
      setRows(cleaned);
    }
  }, [subject_results]);

  // ✅ Handle saving all edited rows
  const handleSaveAll = async () => {
    if (Object.keys(editedRows).length === 0) {
      toast.error("No changes to save.");
      return;
    }

    if (isEditingDisabled) {
      toast.error("Cannot save results. Status is PUBLISHED.");
      return;
    }

    setIsSaving(true);
    try {
      for (const id in editedRows) {
        const originalData = await loadResultsDetails(id);
        const patch = editedRows[id];
        // Ensure empty marks are sent as null so backend stores null (not 0)
        const isEmpty = patch.marks === "" || patch.marks === null || patch.marks === undefined;
        const marksValue = isEmpty ? null : (patch.marks != null ? String(patch.marks) : originalData.marks);
        const updatedData = { ...originalData, ...patch, marks: marksValue };
        await updateSubjectResultsDetails(updatedData);
      }

      await fetch_subject_results({
        teacherId,
        subjectId,
        clazzId,
        examinationTypeId,
      });

      setEditedRows({});
      toast.success("All subject results updated successfully!");
    } catch (error) {
      console.error("Error saving all rows:", error);
      toast.error("An error occurred while saving results.");
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ When editing cells
  const processRowUpdate = (newRow, oldRow) => {
    if (isEditingDisabled) {
      toast.error("Cannot edit results. Status is PUBLISHED.");
      return oldRow;
    }

    const marks =
      newRow.marks !== "" && newRow.marks !== null
        ? Number(newRow.marks)
        : null;

    // ✅ Validate marks
    if (marks !== null && !isNaN(marks) && examMarks !== null) {
      if (marks > examMarks) {
        toast.error(`Marks cannot exceed ${examMarks}.`);
        return oldRow;
      }
      if (marks < 0) {
        toast.error("Marks cannot be negative.");
        return oldRow;
      }
    }

    // ✅ Update local rows & cache edits (use normalized marks so empty → null, not 0)
    const updatedRow = { ...oldRow, ...newRow, marks: marks !== null ? String(marks) : null };
    setEditedRows((prev) => ({
      ...prev,
      [updatedRow.id]: { ...prev[updatedRow.id], ...updatedRow },
    }));

    // ✅ Update the displayed grid
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row))
    );

    return updatedRow;
  };

  // ✅ Columns
  const columns = [
    {
      field: "studentName",
      headerName: "Student Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "marks",
      headerName: "Marks",
      flex: 1,
      editable: !isEditingDisabled,
      align: "left",
      headerAlign: "left",
      minWidth: 60,
      type: "number",
      valueGetter: (value) => {
        if (value === null || value === undefined || value === "") return null;
        return Number(value);
      },
    },
    {
      field: "combination",
      headerName: "Comb",
      flex: 1,
      minWidth: 120,
    },
  ];

  return (
    <Box m="0px">
      <Box p={2} ml={2} mr={2}>
        <Header
          title={`${rows?.[0]?.subjectName || ""} RESULTS FOR ${
            rows?.[0]?.className || ""
          }`}
        />

        {/* ✅ Save button */}
        {!isEditingDisabled && (
          <Box mb={2}>
            <Button
              variant="contained"
              onClick={handleSaveAll}
              disabled={isSaving || Object.keys(editedRows).length === 0}
              sx={{
                backgroundColor: "#1CA9C9",
                color: "#fff",
                textTransform: "none",
                "&:hover": { backgroundColor: "#178aa8" },
                "&.Mui-disabled": {
                  backgroundColor: "#14661aff",
                  color: "#f0f0f0",
                  cursor: "not-allowed",
                },
              }}
            >
              {isSaving && (
                <CircularProgress
                  size={20}
                  color="inherit"
                  sx={{ marginRight: 1 }}
                />
              )}
              Save All
            </Button>
          </Box>
        )}

        {/* ✅ Display info if editing disabled */}
        {isEditingDisabled && (
          <Box mb={2}>
            <Button
              variant="contained"
              disabled
              sx={{
                backgroundColor: "#14661aff",
                color: "#f0f0f0",
                textTransform: "none",
                cursor: "not-allowed",
              }}
            >
              Results Published - Editing Disabled
            </Button>
          </Box>
        )}

        {/* ✅ DataGrid Section */}
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            m="0"
            height="72vh"
            sx={{
              "& .MuiDataGrid-root": {
                color: theme.palette.mode === "light" ? "#000000" : undefined,
                border:
                  theme.palette.mode === "light"
                    ? "1px solid #000000"
                    : undefined,
              },
              "& .MuiDataGrid-row": {
                borderBottom:
                  theme.palette.mode === "light"
                    ? "1px solid #000000"
                    : undefined,
              },
              "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${colors.grey[100]} !important`,
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              disableRowSelectionOnClick
              processRowUpdate={processRowUpdate}
              isCellEditable={(params) =>
                !isEditingDisabled && params.colDef.editable !== false
              }
              experimentalFeatures={{ newEditingApi: true }}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Subject_Results;
